import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminGetFeatureTags } from '@/api/auth/admin/admin';
import type { FeatureTagResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { AddFeatureTagDialog } from '@/components/admin/AddFeatureTagDialog';
import { EditFeatureTagDialog } from '@/components/admin/EditFeatureTagDialog';
import { DeleteFeatureTagDialog } from '@/components/admin/DeleteFeatureTagDialog';

/**
 * Admin feature catalog page. Administrators maintain the available feature tags
 * (product + feature code + description) that organizations can then configure.
 */
export default function AdminFeatureCatalogPage() {
  const { data: featureTagsData, isLoading, error } = useAdminGetFeatureTags();

  const featureTags = featureTagsData?.data?.featureTags || [];

  const [tagToEdit, setTagToEdit] = useState<FeatureTagResponse | null>(null);
  const [tagToDelete, setTagToDelete] = useState<FeatureTagResponse | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Feature Catalog</h1>
          <p className="text-muted-foreground">
            Manage the available product feature tags that organizations can configure
          </p>
        </div>
        <AddFeatureTagDialog />
      </div>

      {error ? (
        <div className="text-center text-destructive py-8">
          <p>Failed to load feature tags: {(error as any)?.message || 'Unknown error'}</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Feature Code</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[140px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="h-4 w-[120px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[180px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[240px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-[120px]" />
                  </TableCell>
                </TableRow>
              ))
            ) : featureTags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No feature tags found. Create your first feature tag to get started.
                </TableCell>
              </TableRow>
            ) : (
              featureTags.map((tag) => (
                <TableRow key={tag.id}>
                  <TableCell className="font-mono">{tag.productId}</TableCell>
                  <TableCell className="font-medium font-mono">{tag.featureCode}</TableCell>
                  <TableCell className="text-muted-foreground">{tag.description}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setTagToEdit(tag)}>
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setTagToDelete(tag)}>
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <EditFeatureTagDialog
        featureTag={tagToEdit}
        open={tagToEdit !== null}
        onOpenChange={(open) => {
          if (!open) {
            setTagToEdit(null);
          }
        }}
      />
      <DeleteFeatureTagDialog
        featureTag={tagToDelete}
        open={tagToDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setTagToDelete(null);
          }
        }}
      />
    </div>
  );
}
